import * as React from 'react';
import { parse } from 'query-string';
import moment from 'moment';
import S3 = require('aws-sdk/clients/s3');
import { History, Location } from 'history';
import { AppUser } from 'Src/views/App/App';
import { Loader } from 'Src/components/Loader/Loader';
import { SelectAllCheckbox } from 'Src/components/SelectAllCheckbox/SelectAllCheckbox';
import { DatePicker as SitDatePicker } from 'Src/components/DatePicker/DatePicker';
import {
  ColumnDefinition,
  TABLE_SORT_DIRECTION,
  getFilters2,
  getOrder,
  getSearchFilter,
  getSearchOrder,
  getServerFieldSectionWhere,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist
} from 'Src/helpers/table/table';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE, LIMIT } from 'Src/consts/table';
import { getAllHouses } from 'Src/helpers/service/admin/houses';
import {
  AwardEvent,
  deleteAwardEvent,
  getArchivedAwardEvents,
  getArchivedAwardEventsCount,
  getAllCurrentYearAwardEvents,
  getCurrentYearAwardEvents,
  getCurrentYearAwardEventsCount,
  updateAwardEvent
} from 'Src/helpers/service/admin/awards';
import {
  addAwardEventPhoto,
  deleteAwardEventPhoto,
  getAllAwardEventPhotos,
  updateAwardEventPhoto
} from 'Src/helpers/service/admin/awardEventPhotos';
import {
  addAwardEventVideo,
  deleteAwardEventVideo,
  getAllAwardEventVideos,
  updateAwardEventVideo
} from 'Src/helpers/service/admin/awardEventVideos';
import { uploadImage } from 'Src/helpers/service/image';
import { getSafeFileName } from 'Src/helpers/file/file';
import { getKey } from 'Src/helpers/service/nobody/key';
import { getVideoConfig } from 'Src/helpers/service/nobody/config';
import { addConvertJob } from 'Src/helpers/service/nobody/aws';
import { Photo } from 'Src/models/photo';
import { Video } from 'Src/models/video';
import { ACCESS_PRESET_TYPE } from 'Src/types/album';
import { AwardEventForm, AwardEventFormValues } from '../AwardEventForm/AwardEventForm';
import { getAllSchoolStudents } from 'Src/helpers/service/admin/students';
import { loadHiddenColumns, saveHiddenColumns } from 'Src/helpers/columnVisibility/columnVisibility';
import { FilterChipSelect } from 'Src/components/FilterChipSelect/FilterChipSelect';
import './AwardEventsCurrentYear.scss';

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

interface State {
  items: AwardEvent[];
  currentPage: number;
  selectedItems: AwardEvent[];
  sortDirection: TABLE_SORT_DIRECTION | '';
  sortColumnsName: string;
  isShowFilter: boolean;
  isDataFiltered: boolean;
  filters: any;
  isLoading: boolean;
  isSelectAllChecked: boolean;
  total: number;
  totalWithFilters: number;
  totalPoints: number;
  totalStudents: number;
  basePath: string;

  isDeleteModalOpen: boolean;
  currentTabIndex: number;

  houses: Array<{ id: string; name: string; pic?: string; status?: string }>;

  isEditable: boolean;
  submitError: string | null;
  formDraft?: Partial<AwardEventFormValues>;

  photos: Photo[];
  videos: Video[];
  isMediaLoading: boolean;
  isUpdateAccessPreset?: boolean;
  isColumnsOpen: boolean;
  hiddenColumns: string[];
  awardView: AwardsView;
}

type AwardsView = 'current' | 'archive';

type PendingVideo = {
  id: string;
  awardId: string;
  fileName: string;
  key?: string;
  status: 'UPLOADING' | 'PROCESSING';
  isLocal: true;
};

const PLACEHOLDER = '/dist/images/empty_pic_uploader_box.png';
const LOGO_SIZE = 40;
const getCurrentAcademicYearLabel = () => {
  const now = new Date();
  const startYear = now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
  return `${startYear}/${startYear + 1}`;
};
const AWARDS_COLUMNS_VIEW_KEY = 'awards.currentYear';
const AWARDS_VIEW_QUERY_KEY = 'awardView';
const AWARDS_TABLE_COLUMNS = [
  { field: 'award', label: 'Award', required: true },
  { field: 'student', label: 'Student' },
  { field: 'house', label: 'House' },
  { field: 'points', label: 'Points' },
  { field: 'awardedAt', label: 'Awarded' },
  { field: 'awardedByName', label: 'Granted by' }
];
const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Award',
    field: 'awardType.name',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    cell: (evt, _unused, idx) => (
      <td key={idx} style={{ display: 'flex', alignItems: 'center', padding: 8 }}>
        <img
          src={evt.displayPicture || evt.awardType?.picture || PLACEHOLDER}
          alt={evt.awardType?.name || 'Award'}
          style={{ width: LOGO_SIZE, height: LOGO_SIZE, borderRadius: '50%', marginRight: 8 }}
        />
        {evt.awardType.name}
      </td>
    )
  },
  {
    text: 'House',
    field: 'houseId',
    isSort: true,
    type: FILTER_TYPE.MULTISELECT,
    accessor: (evt: AwardEvent) => evt.houseName || ''
  },
  {
    text: 'Points',
    field: 'points',
    isSort: true,
    type: FILTER_TYPE.NONE,
    accessor: ['points']
  },
  {
    text: 'Awarded At',
    field: 'awardedAt',
    isSort: true,
    type: FILTER_TYPE.DATE_INTERVAL,
    cell: (evt: AwardEvent, options?: { rowIndex?: number }) => {
      const key = options?.rowIndex ?? evt.id;
      return <td key={key}>{moment(evt.awardedAt).format('DD/MM/YYYY')}</td>;
    }
  },
  {
    text: 'Granted By',
    field: 'awardedByName',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    cell: (evt, _u, idx) => <td key={idx}>{evt.awardedByName}</td>
  },
  {
    text: 'Student',
    field: 'student.displayName',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: (evt: AwardEvent) => evt.student?.displayName || ''
  }
];

const extractUploadedUrl = (val: any): string | undefined => {
  if (!val) return undefined;
  if (typeof val === 'string' && val.length > 0) return val;

  if (val && typeof val === 'object' && 'data' in val) {
    const nested = extractUploadedUrl((val as any).data);
    if (nested) return nested;
  }

  if (typeof (val as any)?.key === 'string' && (val as any).key) {
    const base = (window as any)?.apiImg;
    return base ? `${base}/images/${(val as any).key}` : (val as any).key;
  }

  const directKeys = [
    'url',
    'secure_url',
    'picUrl',
    'pic_url',
    'fileUrl',
    'file_url',
    'location',
    'Location',
    'publicUrl',
    'public_url',
    'path'
  ];
  for (const k of directKeys) {
    const v = (val as any)[k];
    if (typeof v === 'string' && v.length > 0) return v;
  }

  if (Array.isArray(val)) {
    for (const item of val) {
      const u = extractUploadedUrl(item);
      if (u) return u;
    }
  }

  const hasBucketKey =
    (typeof (val as any).Bucket === 'string' && typeof (val as any).Key === 'string') ||
    (typeof (val as any).bucket === 'string' && typeof (val as any).key === 'string');

  if (hasBucketKey) {
    const bucket = (val as any).Bucket || (val as any).bucket;
    const key = (val as any).Key || (val as any).key;
    return `https://${bucket}.s3.amazonaws.com/${key}`;
  }

  return undefined;
};

const normalizeAwardAccess = (p?: ACCESS_PRESET_TYPE): ACCESS_PRESET_TYPE =>
  p === 'PUBLIC' ? 'PRIVATE' : p || 'PRIVATE';

export class AwardEventsCurrentYear extends React.Component<Props, State> {
  private photoInputRef = React.createRef<HTMLInputElement>();
  private videoInputRef = React.createRef<HTMLInputElement>();
  private pendingByAward: Record<string, PendingVideo[]> = {};
  private lastLoadedAwardId?: string;
  private videoPollHandle?: number;
  private videoPollStartedAt?: number;
  private _isMounted = false;
  private filterRef = React.createRef<HTMLDivElement>();
  private columnsRef = React.createRef<HTMLDivElement>();

  private getPending(awardId: string): PendingVideo[] {
    return this.pendingByAward[awardId] || [];
  }
  private setPending(awardId: string, next: PendingVideo[]) {
    this.pendingByAward[awardId] = next;
    this.forceUpdate();
  }

  constructor(props: Props) {
    super(props);
    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      isDataFiltered: false,
      filters: {},
      isLoading: false,
      isSelectAllChecked: false,
      total: 0,
      totalWithFilters: 0,
      totalPoints: 0,
      totalStudents: 0,
      basePath: '',

      isDeleteModalOpen: false,
      currentTabIndex: 0,

      houses: [],

      isEditable: false,
      submitError: null,
      formDraft: undefined,

      photos: [],
      videos: [],
      isMediaLoading: false,
      isUpdateAccessPreset: false,
      isColumnsOpen: false,
      hiddenColumns: loadHiddenColumns(props.user, AWARDS_COLUMNS_VIEW_KEY).filter(
        field => field !== 'award' && AWARDS_TABLE_COLUMNS.some(column => column.field === field)
      ),
      awardView: parse(props.location.search)[AWARDS_VIEW_QUERY_KEY] === 'archive' ? 'archive' : 'current'
    };
  }

  componentDidMount() {
    this._isMounted = true;
    this.setState({ isLoading: true });
    getAllHouses(this.props.user, { status: { $in: ['ACTIVE', 'ARCHIVED'] } })
      .then(houses => this.setState({ houses }))
      .then(() => this.setItems());
    document.addEventListener('mousedown', this.onDocumentMouseDown);
    document.addEventListener('keydown', this.onDocumentKeyDown);
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (
      nextProps.location.search !== this.props.location.search ||
      nextProps.location.pathname !== this.props.location.pathname
    ) {
      this.setState({ isLoading: true });
      this.setItems(nextProps);
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const GALLERY_TAB_INDEX = 1;
    const prevSelectedId = prevState.selectedItems[0]?.id;
    const currSelectedId = this.state.selectedItems[0]?.id;

    const becameMedia =
      prevState.currentTabIndex !== GALLERY_TAB_INDEX && this.state.currentTabIndex === GALLERY_TAB_INDEX;
    const leftMedia =
      prevState.currentTabIndex === GALLERY_TAB_INDEX && this.state.currentTabIndex !== GALLERY_TAB_INDEX;
    const selectionChanged = prevSelectedId !== currSelectedId;

    if (leftMedia) this.stopVideoPolling();
    if (this.state.currentTabIndex === GALLERY_TAB_INDEX && (becameMedia || selectionChanged)) {
      this.loadMediaForSelected();
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.stopVideoPolling();
    document.removeEventListener('mousedown', this.onDocumentMouseDown);
    document.removeEventListener('keydown', this.onDocumentKeyDown);
  }

  onDocumentMouseDown = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    const isCalendarClick = target.closest('.sit-dp__pop') || target.closest('.sit-cal');
    if (
      this.state.isShowFilter &&
      !isCalendarClick &&
      this.filterRef.current &&
      !this.filterRef.current.contains(target)
    ) {
      this.onCancelFilterClick();
    }
    if (this.state.isColumnsOpen && this.columnsRef.current && !this.columnsRef.current.contains(target)) {
      this.setState({ isColumnsOpen: false });
    }
  };

  onDocumentKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return;
    if (this.state.isShowFilter) this.onCancelFilterClick();
    if (this.state.isColumnsOpen) this.setState({ isColumnsOpen: false });
  };

  getAwardsView = (searchValue: string = this.props.history.location.search): AwardsView =>
    parse(searchValue)[AWARDS_VIEW_QUERY_KEY] === 'archive' ? 'archive' : 'current';

  addAwardsViewToSearch = (search: string[], awardView: AwardsView = this.state.awardView) => {
    if (awardView === 'archive') search.push(`${AWARDS_VIEW_QUERY_KEY}=archive`);
  };

  setItems(props: Props = this.props) {
    const { history, user } = props;
    const search = parse(history.location.search);
    const awardView = this.getAwardsView(history.location.search);
    const page = search.page !== undefined ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION | '' = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      const [col, dir] = search.order.split(':');
      sortColumnsName = col || '';
      sortDirection = dir === 'ASC' || dir === 'DESC' ? dir : '';
    }

    const filters = getFilters2(COLUMNS, search);
    const isDataFiltered = isFilterExist2(filters);

    if (awardView === 'archive' && !isDataFiltered) {
      this.setState({
        currentPage: FIRST_PAGE,
        sortDirection,
        sortColumnsName,
        isShowFilter: false,
        isDataFiltered: false,
        filters,
        totalWithFilters: 0,
        totalPoints: 0,
        totalStudents: 0,
        items: [],
        selectedItems: [],
        basePath: history.location.pathname,
        isLoading: false,
        awardView
      });
      return Promise.resolve();
    }

    const filtersForWhere = { ...filters };
    const dateFilter = (filtersForWhere as any).awardedAt;
    delete (filtersForWhere as any).awardedAt;

    const whereRest = getServerFieldSectionWhere(filtersForWhere, { isCorrectTime: true });
    const where: any = { ...whereRest };

    if (dateFilter) {
      if (dateFilter.from) {
        const isoFrom = moment(dateFilter.from, ['DD-MM-YYYY', 'YYYY-MM-DD'])
          .startOf('day')
          .toISOString();
        where.awardedAt = where.awardedAt || {};
        where.awardedAt.$gte = isoFrom;
      }
      if (dateFilter.to) {
        const isoTo = moment(dateFilter.to, ['DD-MM-YYYY', 'YYYY-MM-DD'])
          .endOf('day')
          .toISOString();
        where.awardedAt = where.awardedAt || {};
        where.awardedAt.$lte = isoTo;
      }
    }

    if (Array.isArray((filters as any).houseId) && (filters as any).houseId.length) {
      where.houseId = { $in: (filters as any).houseId };
    }

    const order = sortColumnsName !== '' && sortDirection !== '' ? `${sortColumnsName} ${sortDirection}` : undefined;

    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    const pCountFiltered =
      awardView === 'archive' ? getArchivedAwardEventsCount(user, where) : getCurrentYearAwardEventsCount(user, where);
    const pItems =
      awardView === 'archive'
        ? getArchivedAwardEvents(user, serverQueryFilter)
        : getCurrentYearAwardEvents(user, serverQueryFilter);
    return Promise.all([pCountFiltered, pItems]).then(([summary, items]) => {
      this.setState(
        {
          currentPage: page,
          sortDirection,
          sortColumnsName,
          isShowFilter: false,
          isDataFiltered,
          filters,
          totalWithFilters: summary.count,
          totalPoints: Number(summary.totalPoints) || 0,
          totalStudents: Number(summary.studentCount) || 0,
          items,
          basePath: history.location.pathname,
          isLoading: false,
          awardView
        },
        () => this.fillHouseDecor(items)
      );
    });
  }

  setCurrentPageParams = (currentPage: number) => {
    const { filters, sortColumnsName, sortDirection } = this.state;
    const search: string[] = [];
    if (currentPage !== 1) search.push(`page=${currentPage}`);
    if (isSortExist(sortDirection, sortColumnsName)) search.push(getSearchOrder(sortDirection, sortColumnsName));
    if (isFilterExist2(filters)) search.push(getSearchFilter(filters));
    this.addAwardsViewToSearch(search);
    this.props.history.push({ pathname: this.state.basePath, search: search.join('&') });
  };

  onItemClick = (index: number) => {
    const { items, selectedItems } = this.state;
    const item = items[index];
    const idx = selectedItems.findIndex(si => si.id === item.id);
    const next = [...selectedItems];
    if (idx >= 0) next.splice(idx, 1);
    else next.push(item);
    this.setState({ selectedItems: next });
  };

  onRowClick = (item: AwardEvent) => {
    this.setState({ selectedItems: [item], currentTabIndex: 0 });
  };

  onRowCheckboxClick = (event: React.MouseEvent<HTMLInputElement>, item: AwardEvent) => {
    event.stopPropagation();
    const { selectedItems, items } = this.state;
    const idx = selectedItems.findIndex(si => si.id === item.id);
    const next = [...selectedItems];
    if (idx >= 0) next.splice(idx, 1);
    else next.push(item);
    this.setState({ selectedItems: next, isSelectAllChecked: next.length === items.length });
  };
  onSelectAllOnPageClick = () => {
    const { items, selectedItems } = this.state;
    const toAdd = items.filter(i => !selectedItems.find(si => si.id === i.id));
    this.setState({ selectedItems: [...selectedItems, ...toAdd], isSelectAllChecked: true });
  };

  onSelectAllClick = () => {
    this.setState({ isLoading: true });
    const where = getServerFieldSectionWhere(this.state.filters);
    getAllCurrentYearAwardEvents(this.props.user, where).then(items => {
      const toAdd = items.filter(i => !this.state.selectedItems.find(si => si.id === i.id));
      this.setState({
        selectedItems: [...this.state.selectedItems, ...toAdd],
        isSelectAllChecked: true,
        isLoading: false
      });
    });
  };

  onUnselectAllClick = () => this.setState({ selectedItems: [], isSelectAllChecked: false });

  closeDetailsDrawer = () => this.setState({ selectedItems: [], currentTabIndex: 0 });

  onTableSortClick = (field: string) => {
    const order = getOrder(field, this.state.sortDirection, this.state.sortColumnsName);
    const search: string[] = [`order=${field}:${order}`];
    if (isFilterExist2(this.state.filters)) search.push(getSearchFilter(this.state.filters));
    this.addAwardsViewToSearch(search);
    this.props.history.push({ pathname: this.state.basePath, search: search.join('&') });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];
    let nextFilters = { ...filters };

    if (options && options.modernRange) {
      nextFilters = { ...nextFilters, [filterField]: options.modernRange };
    } else if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = { ...nextFilters, [filterField]: { ...currentFilterField, from: filterValue } };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = { ...nextFilters, [filterField]: { ...currentFilterField, to: filterValue } };
          break;
      }
    } else {
      const filter = COLUMNS.find(col => col.field === filterField);
      const filterType = filter!.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const opts = event.target.options;
        const value: string[] = [];
        for (let i = 0; i < opts.length; i++) if (opts[i].selected) value.push(opts[i].value);
        nextFilters = { ...nextFilters, [filterField]: value };
      } else {
        nextFilters = { ...nextFilters, [filterField]: filterValue };
      }
    }

    this.setState({ filters: nextFilters });
  };

  onApplyFilterClick = () => {
    const { filters, sortColumnsName, sortDirection } = this.state;
    const search: string[] = [];
    if (isSortExist(sortDirection, sortColumnsName)) search.push(getSearchOrder(sortDirection, sortColumnsName));
    if (isFilterExist2(filters)) search.push(getSearchFilter(filters));
    this.addAwardsViewToSearch(search);
    this.setState({ selectedItems: [], isSelectAllChecked: false, isShowFilter: false });
    this.props.history.push({ pathname: this.state.basePath, search: search.join('&') });
  };

  onClearFilterClick = () => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      isShowFilter: false,
      filters: getFilters2(COLUMNS, {})
    });
    const search: string[] = [];
    this.addAwardsViewToSearch(search);
    this.props.history.push({ pathname: this.state.basePath, search: search.join('&') });
  };

  onAwardsViewChange = (awardView: AwardsView) => {
    if (awardView === this.state.awardView) return;
    const search: string[] = [];
    this.addAwardsViewToSearch(search, awardView);
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      isShowFilter: false,
      isColumnsOpen: false,
      filters: getFilters2(COLUMNS, {}),
      awardView,
      isLoading: true
    });
    this.props.history.push({ pathname: '/awards', search: search.join('&') });
  };

  onTableFilterClick = e => {
    e.preventDefault();
    this.setState({ isShowFilter: !this.state.isShowFilter, isColumnsOpen: false });
  };

  isOneItemSelected() {
    return this.state.selectedItems.length === 1;
  }

  onCancelModal = () =>
    this.setState({
      isEditable: false,
      submitError: null,
      formDraft: undefined
    });

  onEditClick = () => {
    const e = this.state.selectedItems[0];
    if (!e) return;
    const dtLocal = moment(e.awardedAt).format('YYYY-MM-DD');

    this.setState({
      isEditable: true,
      submitError: null,
      formDraft: {
        awardedAt: dtLocal,
        points: e.points,
        reason: e.reason || '',
        awardedByName: e.awardedByName || ''
      }
    });
  };

  fetchStudentHouseId = async (studentId?: string | null): Promise<string | null> => {
    if (!studentId) return null;
    try {
      const list = await getAllSchoolStudents(this.props.user, { id: studentId });
      const s = Array.isArray(list) ? list[0] : list;
      if (!s) return null;

      return (
        (s as any).houseId ??
        (s as any)?.house?.id ??
        (Array.isArray((s as any)?.houses) ? (s as any).houses[0]?.id : null) ??
        null
      );
    } catch {
      return null;
    }
  };

  private fillHouseDecor(items: AwardEvent[]) {
    const { houses } = this.state;
    if (!Array.isArray(items) || items.length === 0) return;
    const nameById = new Map<string, string>(houses.map(h => [h.id, h.name]));
    const picById = new Map<string, string | undefined>(houses.map(h => [h.id, h.pic]));

    let changed = false;

    const next = items.map(i => {
      const currentName = (i as any).houseName || '';
      const desiredName = i.houseId ? currentName || nameById.get(i.houseId) || '' : currentName;

      const existingDisp = (i as any).displayPicture;
      const isHousePoints = String(i?.awardType?.name || '').toLowerCase() === 'house points';
      const housePic = i.houseId ? picById.get(i.houseId) || undefined : undefined;

      const desiredDisp = i?.awardType?.picture ? i.awardType.picture : isHousePoints ? housePic : undefined;

      if (desiredName !== currentName || desiredDisp !== existingDisp) {
        changed = true;
        return { ...i, houseName: desiredName, displayPicture: desiredDisp };
      }
      return i;
    });

    if (changed && this._isMounted) {
      this.setState({ items: next });
    }
  }

  onSubmitForm = async (data: AwardEventFormValues) => {
    const { user } = this.props;
    const original = this.state.selectedItems[0];
    if (!original) return;

    const isStudentEvent = !!(original as any)?.student?.id || typeof (original as any)?.studentId === 'string';

    const studentId: string | null = (original as any)?.student?.id || (original as any)?.studentId || null;

    let currentHouseId: string | null = null;
    if (isStudentEvent && studentId) {
      try {
        currentHouseId = await this.fetchStudentHouseId(studentId);
      } catch {
        currentHouseId = null;
      }
    }

    if (currentHouseId == null && isStudentEvent) {
      currentHouseId = ((original as any)?.houseId ?? null) as string | null;
    }

    const pointsNum = Number(data.points ?? 0);

    if (isStudentEvent && pointsNum !== 0 && !currentHouseId) {
      this.setState({
        submitError:
          'This student isn’t assigned to a house yet. To save, either set Points to 0 or assign them to a house.',
        isLoading: false
      });
      return;
    }

    const payload: AwardEventFormValues & { houseId?: string | null } = {
      ...data,
      ...(isStudentEvent ? { houseId: currentHouseId ?? null } : {})
    };

    this.setState({ isLoading: true, submitError: null });

    try {
      await updateAwardEvent(user, payload, original.id);
      await this.setItems();
      const updated = this.state.items.find(i => i.id === original.id);
      if (!updated) throw new Error('Updated award not found');

      this.setState({
        isEditable: false,
        selectedItems: [updated],
        isLoading: false,
        formDraft: undefined
      });
    } catch (err) {
      const e = err as any;
      const serverText = e?.response?.data?.details?.text || e?.response?.data?.error || e?.message || '';
      const isHouseValidation = /house/i.test(serverText) && /point/i.test(serverText);

      this.setState({
        isLoading: false,
        submitError: isHouseValidation
          ? 'This student isn’t assigned to a house yet. To save, either set Points to 0 or assign them to a house.'
          : 'Something went wrong. Please try again.',
        formDraft: data
      });
    }
  };

  onDeleteModalOpen = () => this.setState({ isDeleteModalOpen: true });
  onDeleteModalClose = () => this.setState({ isDeleteModalOpen: false });

  onDeleteConfirm = () => {
    const ids = this.state.selectedItems.map(i => i.id);
    this.setState({ isLoading: true });
    Promise.all(ids.map(id => deleteAwardEvent(this.props.user, id)))
      .then(() => {
        this.setState({ isDeleteModalOpen: false, selectedItems: [], isLoading: false });
        return this.setItems();
      })
      .catch(err => {
        this.setState({ isLoading: false });
        console.error(err);
      });
  };

  isVideoPlayable = (v: Video) => v?.status === 'COMPLETE' && Array.isArray(v?.streamData) && v.streamData.length > 0;

  startVideoPolling = (awardId: string) => {
    this.stopVideoPolling();
    this.videoPollStartedAt = Date.now();

    this.videoPollHandle = window.setInterval(async () => {
      const GALLERY_TAB_INDEX = 1;
      const stillOnMedia = this.state.currentTabIndex === GALLERY_TAB_INDEX;
      const sameAward = this.state.selectedItems[0]?.id === awardId;
      if (!stillOnMedia || !sameAward) return this.stopVideoPolling();

      try {
        const fetched = await getAllAwardEventVideos(this.props.user, awardId);
        const videos = Array.isArray(fetched)
          ? fetched
          : fetched && Array.isArray((fetched as any).items)
          ? (fetched as any).items
          : [];

        const allPlayable = videos.length === 0 || videos.every(this.isVideoPlayable);
        this.setState({ videos });

        const tooLong = Date.now() - (this.videoPollStartedAt || 0) > 5 * 60 * 1000;
        if (allPlayable || tooLong) this.stopVideoPolling();
      } catch (e) {
        console.warn('[video-poll] fetch failed', e);
      }
    }, 5000);
  };

  stopVideoPolling = () => {
    if (this.videoPollHandle) {
      window.clearInterval(this.videoPollHandle);
      this.videoPollHandle = undefined;
    }
  };

  loadMediaForSelected = async () => {
    const award = this.state.selectedItems[0];
    if (!award) return;

    if (this.lastLoadedAwardId === award.id) {
      if (this.state.isMediaLoading) this.setState({ isMediaLoading: false });
      return;
    }

    this.setState({ isMediaLoading: true });

    const [rawPhotos, rawVideos] = await Promise.all([
      getAllAwardEventPhotos(this.props.user, award.id).catch(() => []),
      getAllAwardEventVideos(this.props.user, award.id).catch(() => [])
    ]);

    const photos = Array.isArray(rawPhotos)
      ? rawPhotos
      : rawPhotos && Array.isArray((rawPhotos as any).items)
      ? (rawPhotos as any).items
      : [];
    const videos = Array.isArray(rawVideos)
      ? rawVideos
      : rawVideos && Array.isArray((rawVideos as any).items)
      ? (rawVideos as any).items
      : [];

    this.lastLoadedAwardId = award.id;

    this.setState({ photos, videos, isMediaLoading: false }, () => {
      const needsPoll = videos.some(v => !this.isVideoPlayable(v));
      if (needsPoll) this.startVideoPolling(award.id);
      else this.stopVideoPolling();
    });
  };

  onDeleteAwardPhoto = (photoId: string) => {
    const award = this.state.selectedItems[0];
    if (!award) return;
    this.setState({ isMediaLoading: true });
    deleteAwardEventPhoto(this.props.user, award.id, photoId)
      .then(() => getAllAwardEventPhotos(this.props.user, award.id))
      .then(photos => this.setState({ photos, isMediaLoading: false }))
      .catch(err => {
        console.error('Delete photo failed', err);
        this.setState({ isMediaLoading: false });
      });
  };

  onDeleteAwardVideo = (videoId: string) => {
    const award = this.state.selectedItems[0];
    if (!award) return;
    this.setState({ isMediaLoading: true });
    deleteAwardEventVideo(this.props.user, award.id, videoId)
      .then(() => getAllAwardEventVideos(this.props.user, award.id))
      .then(videos => this.setState({ videos, isMediaLoading: false }))
      .catch(err => {
        console.error('Delete video failed', err);
        this.setState({ isMediaLoading: false });
      });
  };

  triggerPhotoPicker = () => this.photoInputRef.current?.click();
  triggerVideoPicker = () => this.videoInputRef.current?.click();

  onPhotoFilesSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const files = Array.from(input.files || []);
    input.value = '';
    const award = this.state.selectedItems[0];
    if (!files.length || !award) return;

    this.setState({ isMediaLoading: true });
    try {
      for (const file of files) {
        const resp = await uploadImage(this.props.user, file);
        const picUrl = extractUploadedUrl(resp);
        if (!picUrl) throw new Error('uploadImage returned no usable URL');
        const accessPreset = normalizeAwardAccess(
          this.props.user.activeSchool?.photoAccessPreset as ACCESS_PRESET_TYPE
        );
        await addAwardEventPhoto(this.props.user, award.id, {
          picUrl,
          originalFileName: file.name,
          accessPreset
        });
      }

      const raw = await getAllAwardEventPhotos(this.props.user, award.id);

      const photos = Array.isArray(raw) ? raw : raw && Array.isArray((raw as any).items) ? (raw as any).items : [];
      this.setState({ photos, isMediaLoading: false });
    } catch (err) {
      console.error('Add photo(s) failed', err);
      this.setState({ isMediaLoading: false });
    }
  };

  onVideoFilesSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const files = Array.from(input.files || []);
    input.value = '';
    const award = this.state.selectedItems[0];
    if (!files.length || !award) return;

    this.setState({ isMediaLoading: true });
    try {
      const videoConfig: any = await getVideoConfig(this.props.user);
      const { accessKey, secretAccessKey, bucketRegion, bucketName } = videoConfig;

      const s3 = new S3({
        region: bucketRegion,
        credentials: { accessKeyId: accessKey, secretAccessKey }
      });

      const placeholders: PendingVideo[] = files.map((file, idx) => ({
        id: `temp-${Date.now()}-${idx}`,
        awardId: award.id,
        fileName: file.name,
        status: 'UPLOADING',
        isLocal: true
      }));
      this.setPending(award.id, [...this.getPending(award.id), ...placeholders]);

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const safeName = getSafeFileName(file.name);
        const tempId = placeholders[i].id;

        const keyObj: any = await getKey(this.props.user);
        const folderKey: string = keyObj?.key;
        if (!folderKey) throw new Error('getKey did not return a "key"');

        {
          const p = this.getPending(award.id);
          const idx = p.findIndex(v => v.id === tempId);
          if (idx >= 0) {
            p[idx] = { ...p[idx], key: folderKey };
            this.setPending(award.id, p);
          }
        }

        const folderName = `orig/${folderKey}/`;
        const videoFullName = `${folderName}${safeName}`;

        await s3.putObject({ Key: folderName, Bucket: bucketName }).promise();
        await s3.putObject({ Key: videoFullName, Bucket: bucketName, Body: file, ContentType: file.type }).promise();

        {
          const p = this.getPending(award.id);
          const idx = p.findIndex(v => v.id === tempId);
          if (idx >= 0) {
            p[idx] = { ...p[idx], status: 'PROCESSING' };
            this.setPending(award.id, p);
          }
        }

        const convertResp: any = await addConvertJob(this.props.user, {
          key: folderKey,
          fileName: safeName,
          bucketName
        });
        const jobId: string = convertResp?.Job?.Id || convertResp?.jobId || convertResp?.id;
        if (!jobId) throw new Error('addConvertJob returned no Job.Id');

        const accessPreset = normalizeAwardAccess(
          this.props.user.activeSchool?.photoAccessPreset as ACCESS_PRESET_TYPE
        );
        await addAwardEventVideo(this.props.user, award.id, {
          fileName: safeName,
          key: folderKey,
          jobId,
          accessPreset
        });
      }

      const raw = await getAllAwardEventVideos(this.props.user, award.id);
      const serverVideos = Array.isArray(raw)
        ? raw
        : raw && Array.isArray((raw as any).items)
        ? (raw as any).items
        : [];

      const serverKeys = new Set((serverVideos || []).map((v: any) => (v.key || '').toString()));
      const serverNames = new Set((serverVideos || []).map((v: any) => (v.fileName || '').toLowerCase()));

      const pending = this.getPending(award.id);
      const remaining = pending.filter(p => {
        if (p.key) return !serverKeys.has(p.key);
        return !serverNames.has(p.fileName.toLowerCase());
      });
      this.setPending(award.id, remaining);

      this.setState({ videos: serverVideos, isMediaLoading: false }, () => this.startVideoPolling(award.id));
    } catch (err) {
      console.error('Add video(s) failed', err);
      this.setState({ isMediaLoading: false });
    }
  };

  onAccessPresetPhotoClick = async (photoId: string, accessPreset: ACCESS_PRESET_TYPE) => {
    const award = this.state.selectedItems[0];
    if (!award) return;
    this.setState({ isUpdateAccessPreset: true });
    try {
      const updated = await updateAwardEventPhoto(this.props.user, award.id, photoId, { accessPreset });
      this.setState(({ photos }) => ({
        photos: (photos || []).map(p => (p.id === photoId ? updated : p)),
        isUpdateAccessPreset: false
      }));
    } catch (e) {
      console.error('Update photo access failed', e);
      this.setState({ isUpdateAccessPreset: false });
    }
  };

  onAccessPresetVideoClick = async (videoId: string, accessPreset: ACCESS_PRESET_TYPE) => {
    const award = this.state.selectedItems[0];
    if (!award) return;
    this.setState({ isUpdateAccessPreset: true });
    try {
      const updated = await updateAwardEventVideo(this.props.user, award.id, videoId, { accessPreset });
      this.setState(({ videos }) => ({
        videos: (videos || []).map(v => (v.id === videoId ? updated : v)),
        isUpdateAccessPreset: false
      }));
    } catch (e) {
      console.error('Update video access failed', e);
      this.setState({ isUpdateAccessPreset: false });
    }
  };

  onUpdatePhotoMeta = async (photoId: string, patch: { name?: string; description?: string }) => {
    const award = this.state.selectedItems[0];
    if (!award) return;
    try {
      const updated = await updateAwardEventPhoto(this.props.user, award.id, photoId, patch);
      this.setState(({ photos }) => ({ photos: photos.map(p => (p.id === photoId ? updated : p)) }));
    } catch (e) {
      console.error('Update photo meta failed', e);
    }
  };

  onUpdateVideoMeta = async (videoId: string, patch: { name?: string; description?: string }) => {
    const award = this.state.selectedItems[0];
    if (!award) return;
    try {
      const updated = await updateAwardEventVideo(this.props.user, award.id, videoId, patch);
      this.setState(({ videos }) => ({ videos: videos.map(v => (v.id === videoId ? updated : v)) }));
    } catch (e) {
      console.error('Update video meta failed', e);
    }
  };

  renderEditModal() {
    const { isEditable, submitError, formDraft, selectedItems } = this.state;
    const initial = formDraft as AwardEventFormValues | undefined;
    const award = selectedItems[0];
    if (!isEditable || !initial || !award) return null;

    const recipient = award.student?.displayName || (award.houseName ? `${award.houseName} house` : 'School award');

    return (
      <div className="schoolAwardsModalOverlay" onMouseDown={this.onCancelModal}>
        <div
          className="schoolAwardsModal schoolAwardsModal--edit"
          onMouseDown={event => event.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          <div className="schoolAwardsModal__head">
            {this.renderAwardEmblem(
              award.awardType?.name || 'Award',
              42,
              (award as any).displayPicture || award.awardType?.picture
            )}
            <div>
              <h2>Edit award</h2>
              <p>
                {award.awardType?.name || 'Award'} · {recipient}
              </p>
            </div>
            <button type="button" className="schoolAwardsModal__close" onClick={this.onCancelModal} aria-label="Close">
              ×
            </button>
          </div>
          <div className="schoolAwardsModal__body">
            {submitError && <div className="schoolAwardsFormError">{submitError}</div>}
            <AwardEventForm
              initialValues={initial}
              user={this.props.user}
              onCancel={this.onCancelModal}
              onSubmit={this.onSubmitForm}
            />
          </div>
        </div>
      </div>
    );
  }
  getAwardTone = (type?: string) => {
    const name = String(type || '').toLowerCase();
    if (name.indexOf('match') !== -1 || name.indexOf('trophy') !== -1) {
      return {
        color: '#227ebd',
        icon: 'M8 21h8M12 17v4M7 4h10v4a5 5 0 0 1-10 0V4zM5 4H3v2a3 3 0 0 0 3 3M19 4h2v2a3 3 0 0 1-3 3'
      };
    }
    if (name.indexOf('sport') !== -1) {
      return {
        color: '#2f9e5a',
        icon:
          'M8 13V5a1.5 1.5 0 0 1 3 0v6M11 11V4a1.5 1.5 0 0 1 3 0v7M14 11V6a1.5 1.5 0 0 1 3 0v8a6 6 0 0 1-6 6H9a5 5 0 0 1-4-2l-3-4a1.6 1.6 0 0 1 2.5-2L8 12'
      };
    }
    if (name.indexOf('academic') !== -1) {
      return { color: '#7b5bd6', icon: 'M22 9 12 4 2 9l10 5 10-5zM6 11.5V17c0 1.1 2.7 3 6 3s6-1.9 6-3v-5.5M22 9v6' };
    }
    if (name.indexOf('community') !== -1) {
      return {
        color: '#d64550',
        icon:
          'M20.8 5.6a5 5 0 0 0-7-.2L12 6.9l-1.7-1.5a5 5 0 1 0-6.7 7.4l7.6 7.6a1 1 0 0 0 1.5 0l7.6-7.6a5 5 0 0 0 .1-7.2z'
      };
    }
    if (name.indexOf('conduct') !== -1) return { color: '#0f8a8a', icon: 'M13 2 3 14h7l-1 8 10-12h-7z' };
    if (name.indexOf('effort') !== -1) {
      return {
        color: '#f0932b',
        icon: 'M12 2s5 4 5 9a5 5 0 0 1-10 0c0-1.5.7-2.8 1.5-3.7C9 9 9.5 10 10 10.5c0-2 1-4.5 2-8.5z'
      };
    }
    return { color: '#227ebd', icon: 'M12 2l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17.8 5.9 20.6l1.4-6.8L2.2 9.1l6.9-.8z' };
  };

  onCancelFilterClick = () => {
    this.setState({
      isShowFilter: false,
      filters: getFilters2(COLUMNS, parse(this.props.history.location.search))
    });
  };

  toggleHouseFilter = (houseId: string) => {
    const current = Array.isArray(this.state.filters.houseId) ? this.state.filters.houseId.map(String) : [];
    const next = current.includes(houseId) ? current.filter(id => id !== houseId) : [...current, houseId];
    this.setState({ filters: { ...this.state.filters, houseId: next } });
  };

  getAvailableFilterHouses = () =>
    this.state.houses.filter(house => this.state.awardView === 'archive' || house.status === 'ACTIVE');

  renderAwardEmblem = (type?: string, size = 36, picture?: string) => {
    if (picture) {
      return (
        <img
          className="schoolAwardsAwardImage"
          src={picture}
          alt={type || 'Award'}
          style={{ width: size, height: size, borderRadius: Math.round(size / 3.6) }}
        />
      );
    }

    const tone = this.getAwardTone(type);
    return (
      <span
        className="schoolAwardsEmblem"
        style={{
          width: size,
          height: size,
          borderRadius: Math.round(size / 3.6),
          backgroundColor: `${tone.color}1a`,
          color: tone.color
        }}
      >
        <svg
          viewBox="0 0 24 24"
          width={Math.round(size * 0.55)}
          height={Math.round(size * 0.55)}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d={tone.icon} />
        </svg>
      </span>
    );
  };

  renderPointsPill = (points: number) => {
    const negative = Number(points) < 0;
    return (
      <span className={negative ? 'schoolAwardsPointsPill schoolAwardsPointsPill--negative' : 'schoolAwardsPointsPill'}>
        <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" aria-hidden="true">
          <path d="M12 2l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17.8 5.9 20.6l1.4-6.8L2.2 9.1l6.9-.8z" />
        </svg>
        {points > 0 ? '+' : ''}
        {points} pts
      </span>
    );
  };
  renderFilters() {
    const { filters } = this.state;
    const houses = this.getAvailableFilterHouses();
    const awardName = filters['awardType.name'] || '';
    const awardedByName = filters.awardedByName || '';
    const houseIdList = Array.isArray(filters.houseId) ? filters.houseId : [];
    const awardedAt = filters.awardedAt || {};

    return (
      <div className="schoolAwardsFilters">
        <header>
          <strong>Filters</strong>
          <button type="button" onClick={this.onCancelFilterClick} aria-label="Close filters">
            ×
          </button>
        </header>
        <div
          className="schoolAwardsFilters__body"
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              this.onApplyFilterClick();
            }
          }}
        >
          <label>
            <span>Award</span>
            <input
              value={awardName}
              onChange={event => this.onTableFilterChange(event, 'awardType.name')}
              placeholder="Search award"
            />
          </label>
          <label>
            <span>Granted by</span>
            <input
              value={awardedByName}
              onChange={event => this.onTableFilterChange(event, 'awardedByName')}
              placeholder="Search staff member"
            />
          </label>
          <SitDatePicker
            label="Awarded at interval"
            mode="range"
            value={{
              start: awardedAt.from ? moment(awardedAt.from, ['DD-MM-YYYY', 'YYYY-MM-DD']).toDate() : null,
              end: awardedAt.to ? moment(awardedAt.to, ['DD-MM-YYYY', 'YYYY-MM-DD']).toDate() : null
            }}
            onChange={(range: any) =>
              this.onTableFilterChange({ target: { value: '' } }, 'awardedAt', {
                modernRange: {
                  from: range && range.start ? moment(range.start).format('DD-MM-YYYY') : '',
                  to: range && range.end ? moment(range.end).format('DD-MM-YYYY') : ''
                }
              })
            }
            format="DD-MM-YYYY"
            numberOfMonths={2}
            clearable={true}
            syncRangeOnSelect={true}
          />
          <FilterChipSelect
            label="House"
            className="schoolAwardsFilters__houses"
            selected={houseIdList.map(String)}
            collapsedLimit={6}
            options={houses.map(house => ({ value: String(house.id), label: String(house.name) }))}
            onToggle={this.toggleHouseFilter}
          />
        </div>
        <footer>
          <button type="button" className="is-clear" onClick={this.onClearFilterClick}>
            Clear all
          </button>
          <div>
            <button type="button" onClick={this.onCancelFilterClick}>
              Cancel
            </button>
            <button type="button" className="is-apply" onClick={this.onApplyFilterClick}>
              Apply
            </button>
          </div>
        </footer>
      </div>
    );
  }

  renderActiveFilters() {
    const { filters } = this.state;
    const houses = this.getAvailableFilterHouses();
    const chips: Array<{ field: string; label: string; value: string }> = [];
    if (filters['awardType.name']) {
      chips.push({ field: 'awardType.name', label: 'Award', value: filters['awardType.name'] });
    }
    if (filters['student.displayName']) {
      chips.push({ field: 'student.displayName', label: 'Student', value: filters['student.displayName'] });
    }
    if (filters.awardedByName) {
      chips.push({ field: 'awardedByName', label: 'Granted by', value: filters.awardedByName });
    }
    if (filters.awardedAt && (filters.awardedAt.from || filters.awardedAt.to)) {
      chips.push({
        field: 'awardedAt',
        label: 'Awarded at',
        value: [filters.awardedAt.from, filters.awardedAt.to].filter(Boolean).join(' – ')
      });
    }
    const houseIds = Array.isArray(filters.houseId) ? filters.houseId.map(String) : [];
    if (houseIds.length) {
      chips.push({
        field: 'houseId',
        label: 'House',
        value: houses
          .filter(house => houseIds.includes(String(house.id)))
          .map(house => house.name)
          .join(', ')
      });
    }
    if (!chips.length) return null;
    return (
      <div className="schoolAwardsActiveFilters">
        {chips.map(chip => (
          <span key={chip.field}>
            <b>{chip.label}:</b> {chip.value}
            <button
              type="button"
              onClick={() => this.removeActiveFilter(chip.field)}
              aria-label={`Remove ${chip.label} filter`}
            >
              ×
            </button>
          </span>
        ))}
        <button type="button" onClick={this.onClearFilterClick}>
          Clear all
        </button>
      </div>
    );
  }

  getActiveFilterCount = () => {
    const { filters } = this.state;
    return [
      filters['awardType.name'],
      filters['student.displayName'],
      filters.awardedByName,
      filters.awardedAt && (filters.awardedAt.from || filters.awardedAt.to),
      Array.isArray(filters.houseId) && filters.houseId.length > 0
    ].filter(Boolean).length;
  };

  removeActiveFilter = (field: string) => {
    const emptyValue = field === 'houseId' ? [] : field === 'awardedAt' ? { from: '', to: '' } : '';
    this.setState(state => ({ filters: { ...state.filters, [field]: emptyValue } }), this.onApplyFilterClick);
  };

  toggleColumn = (field: string) => {
    if (field === 'award') return;
    this.setState(state => {
      const hiddenColumns = state.hiddenColumns.includes(field)
        ? state.hiddenColumns.filter(column => column !== field)
        : [...state.hiddenColumns, field];
      saveHiddenColumns(this.props.user, AWARDS_COLUMNS_VIEW_KEY, hiddenColumns);
      return { hiddenColumns };
    });
  };

  resetColumns = () => {
    saveHiddenColumns(this.props.user, AWARDS_COLUMNS_VIEW_KEY, []);
    this.setState({ hiddenColumns: [] });
  };

  renderSelectionBar() {
    const selectedCount = this.state.selectedItems.length;
    if (!selectedCount) return null;

    return (
      <div className="schoolAwardsSelectionBar">
        <strong>{selectedCount} selected</strong>
        {this.isOneItemSelected() && (
          <button type="button" className="schoolAwardsButton schoolAwardsButton--secondary" onClick={this.onEditClick}>
            Edit award
          </button>
        )}
        <button
          type="button"
          className="schoolAwardsButton schoolAwardsButton--danger"
          onClick={this.onDeleteModalOpen}
        >
          Delete award
        </button>
        <button type="button" className="schoolAwardsSelectionBar__clear" onClick={this.onUnselectAllClick}>
          Clear selection
        </button>
      </div>
    );
  }

  renderTable() {
    const {
      items,
      selectedItems,
      sortColumnsName,
      sortDirection,
      currentPage,
      totalWithFilters,
      hiddenColumns,
      isColumnsOpen,
      awardView,
      isDataFiltered
    } = this.state;
    const pageSize = LIMIT;
    const pageCount = Math.max(1, Math.ceil(totalWithFilters / pageSize));
    const isFirst = currentPage <= 1;
    const isLast = currentPage >= pageCount;
    const selectedIds = new Set(selectedItems.map(item => item.id));
    const isArchiveAwaitingFilters = awardView === 'archive' && !isDataFiltered;
    const isColumnVisible = (field: string) => !hiddenColumns.includes(field);
    const configurableColumns = AWARDS_TABLE_COLUMNS;

    const sortLabel = (field: string) => (sortColumnsName === field ? (sortDirection === 'ASC' ? ' ↑' : ' ↓') : '');

    return (
      <section className="schoolAwardsTableCard">
        <div className="schoolAwardsToolbar">
          <label className="schoolAwardsSearch">
            <input
              value={this.state.filters['student.displayName'] || ''}
              placeholder="Search by first or last name"
              onChange={event => this.onTableFilterChange(event, 'student.displayName')}
              onKeyDown={event => event.key === 'Enter' && this.onApplyFilterClick()}
            />
          </label>
          <div className="schoolAwardsToolbar__actions">
            <div className="schoolAwardsFilterAnchor" ref={this.filterRef}>
              <button
                type="button"
                className={
                  this.state.isShowFilter || this.state.isDataFiltered
                    ? 'schoolAwardsFilterButton is-active'
                    : 'schoolAwardsFilterButton'
                }
                onClick={this.onTableFilterClick}
              >
                <i /> Filters
                {this.getActiveFilterCount() > 0 && <b>{this.getActiveFilterCount()}</b>}
              </button>
              {this.state.isShowFilter && this.renderFilters()}
            </div>
            <div className="schoolAwardsColumns" ref={this.columnsRef}>
              <button
                type="button"
                className={isColumnsOpen ? 'schoolAwardsColumns__button is-active' : 'schoolAwardsColumns__button'}
                onClick={() => this.setState({ isColumnsOpen: !isColumnsOpen, isShowFilter: false })}
              >
                <i /> Columns <b>{configurableColumns.length - hiddenColumns.length}</b>
              </button>
              {isColumnsOpen && (
                <div className="schoolAwardsColumns__menu">
                  <header>
                    <strong>Show / hide columns</strong>
                    <button type="button" onClick={this.resetColumns}>
                      Reset
                    </button>
                  </header>
                  {configurableColumns.map(column => (
                    <label key={column.field}>
                      <input
                        type="checkbox"
                        checked={isColumnVisible(column.field)}
                        disabled={Boolean(column.required)}
                        onChange={() => this.toggleColumn(column.field)}
                      />
                      {column.label}
                      {column.required && <small>Required</small>}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {this.renderActiveFilters()}
        {this.renderSelectionBar()}

        <div className="schoolAwardsTableWrap">
          <table className="schoolAwardsTable">
            <thead>
              <tr>
                <th className="schoolAwardsTable__check" aria-label="Select">
                  <SelectAllCheckbox
                    compact
                    isChecked={items.length > 0 && items.every(item => selectedIds.has(item.id))}
                    totalCount={totalWithFilters}
                    currentPage={currentPage}
                    pageItemsCount={items.length}
                    selectedItems={selectedItems}
                    onSelectAllOnPageClick={this.onSelectAllOnPageClick}
                    onSelectAllClick={this.onSelectAllClick}
                    onUnselectAllClick={this.onUnselectAllClick}
                  />
                </th>
                <th>Award</th>
                {isColumnVisible('student') && <th>Student</th>}
                {isColumnVisible('house') && <th>House</th>}
                {isColumnVisible('points') && (
                  <th>
                    <button type="button" onClick={() => this.onTableSortClick('points')}>
                      Points{sortLabel('points')}
                    </button>
                  </th>
                )}
                {isColumnVisible('awardedAt') && (
                  <th>
                    <button type="button" onClick={() => this.onTableSortClick('awardedAt')}>
                      Awarded{sortLabel('awardedAt')}
                    </button>
                  </th>
                )}
                {isColumnVisible('awardedByName') && (
                  <th>
                    <button type="button" onClick={() => this.onTableSortClick('awardedByName')}>
                      Granted by{sortLabel('awardedByName')}
                    </button>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {items.map(item => {
                const isSelected = selectedIds.has(item.id);
                const picture = (item as any).displayPicture || item.awardType?.picture || PLACEHOLDER;
                return (
                  <tr
                    key={item.id}
                    className={
                      isSelected ? 'schoolAwardsTable__row schoolAwardsTable__row--selected' : 'schoolAwardsTable__row'
                    }
                    onClick={() => this.onRowClick(item)}
                  >
                    <td className="schoolAwardsTable__check">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onClick={event => this.onRowCheckboxClick(event, item)}
                        onChange={() => undefined}
                      />
                    </td>
                    <td>
                      <div className="schoolAwardsAwardCell">
                        {this.renderAwardEmblem(item.awardType?.name || 'Award', 36, picture)}
                        <div>
                          <strong>{item.awardType?.name || 'Award'}</strong>
                          <span>{item.reason || 'Award granted this academic year'}</span>
                        </div>
                      </div>
                    </td>
                    {isColumnVisible('student') && (
                      <td>
                        {item.student?.displayName ? (
                          <span className="schoolAwardsRecipient">{item.student.displayName}</span>
                        ) : (
                          <span className="schoolAwardsRecipient schoolAwardsRecipient--house">
                            {item.houseName || 'Whole house'}
                          </span>
                        )}
                      </td>
                    )}
                    {isColumnVisible('house') && (
                      <td>
                        {item.houseName ? (
                          <span className="schoolAwardsHouseChip">
                            <i />
                            {item.houseName}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                    )}
                    {isColumnVisible('points') && <td>{this.renderPointsPill(item.points)}</td>}
                    {isColumnVisible('awardedAt') && <td>{moment(item.awardedAt).format('DD MMM YYYY')}</td>}
                    {isColumnVisible('awardedByName') && <td>{item.awardedByName || '—'}</td>}
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr>
                  <td colSpan={configurableColumns.length - hiddenColumns.length + 1}>
                    <div className="schoolAwardsEmpty">
                      <div>★</div>
                      <h3>{isArchiveAwaitingFilters ? 'Choose filters to search the archive' : 'No awards found'}</h3>
                      <p>
                        {isArchiveAwaitingFilters
                          ? 'Use a date interval, student, award, house or granted-by filter to load archived awards.'
                          : `No ${
                              awardView === 'archive' ? 'archived' : 'current academic year'
                            } awards match your filters.`}
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {!isArchiveAwaitingFilters && (
          <nav className="schoolAwardsPagination" aria-label="Awards pagination">
            <button type="button" disabled={isFirst} onClick={() => this.setCurrentPageParams(currentPage - 1)}>
              Previous
            </button>
            <span className="schoolAwardsPagination__info">
              Page {currentPage} of {pageCount}
            </span>
            <button type="button" disabled={isLast} onClick={() => this.setCurrentPageParams(currentPage + 1)}>
              Next
            </button>
          </nav>
        )}
      </section>
    );
  }

  getVideoStreamUrl = (video: any): string =>
    video && Array.isArray(video.streamData) && video.streamData[0]?.url ? video.streamData[0].url : '';

  renderGalleryTab = () => {
    const { selectedItems, photos, videos, isMediaLoading } = this.state;
    if (selectedItems.length !== 1) return null;

    const awardId = selectedItems[0].id;
    const pendingForThisAward = this.getPending(awardId);
    const displayVideos: any[] = [...pendingForThisAward, ...videos];
    const mediaCount = photos.length + displayVideos.length;

    const notPlayableCount = displayVideos.filter((v: any) => !this.isVideoPlayable(v)).length;

    return (
      <div className="schoolAwardsGallery">
        <input
          ref={this.photoInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={this.onPhotoFilesSelected}
        />
        <input
          ref={this.videoInputRef}
          type="file"
          accept="video/*"
          multiple
          style={{ display: 'none' }}
          onChange={this.onVideoFilesSelected}
        />

        <div className="schoolAwardsGallery__toolbar">
          <div>
            <strong>Gallery</strong>
            <span>
              {mediaCount ? `${mediaCount} media item${mediaCount === 1 ? '' : 's'}` : 'No photos or videos yet'}
            </span>
          </div>
          <div>
            <button
              type="button"
              className="schoolAwardsButton schoolAwardsButton--secondary"
              onClick={this.triggerPhotoPicker}
            >
              <svg
                viewBox="0 0 24 24"
                width="15"
                height="15"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <circle cx="8.5" cy="10.5" r="1.5" />
                <path d="M21 15l-5-5L5 21" />
              </svg>
              Add photos
            </button>
            <button
              type="button"
              className="schoolAwardsButton schoolAwardsButton--secondary"
              onClick={this.triggerVideoPicker}
            >
              <svg
                viewBox="0 0 24 24"
                width="15"
                height="15"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="m22 8-6 4 6 4V8Z" />
                <rect x="2" y="6" width="14" height="12" rx="2" />
              </svg>
              Add videos
            </button>
          </div>
        </div>

        {isMediaLoading && (
          <div className="schoolAwardsGallery__loading">
            <Loader />
          </div>
        )}

        {!isMediaLoading && notPlayableCount > 0 && (
          <div className="schoolAwardsNotice">
            {notPlayableCount} video{notPlayableCount > 1 ? 's are' : ' is'} uploading or processing. This list updates
            automatically.
          </div>
        )}

        {!isMediaLoading && mediaCount === 0 && (
          <div className="schoolAwardsGallery__empty">
            <span>
              <svg
                viewBox="0 0 24 24"
                width="30"
                height="30"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <circle cx="8.5" cy="10.5" r="1.5" />
                <path d="M21 15l-5-5L5 21" />
              </svg>
            </span>
            <p>No photos or videos on this award yet.</p>
            <button
              type="button"
              className="schoolAwardsButton schoolAwardsButton--primary"
              onClick={this.triggerPhotoPicker}
            >
              <svg
                viewBox="0 0 24 24"
                width="15"
                height="15"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
              Add media
            </button>
          </div>
        )}

        {!isMediaLoading && mediaCount > 0 && (
          <div className="schoolAwardsGallery__grid">
            {photos.map(photo => (
              <div className="schoolAwardsGalleryTile" key={photo.id}>
                <img src={photo.picUrl} alt={photo.name || 'Award photo'} />
                <button type="button" onClick={() => this.onDeleteAwardPhoto(photo.id)} aria-label="Delete photo">
                  ×
                </button>
              </div>
            ))}
            {displayVideos.map(video => {
              const playable = this.isVideoPlayable(video);
              const streamUrl = playable ? this.getVideoStreamUrl(video) : '';
              const isProcessing = !playable;
              return (
                <div
                  className={
                    isProcessing
                      ? 'schoolAwardsGalleryTile schoolAwardsGalleryTile--processing'
                      : 'schoolAwardsGalleryTile'
                  }
                  key={video.id}
                >
                  {playable ? <video src={streamUrl} controls preload="metadata" /> : <span>Processing</span>}
                  <button type="button" onClick={() => this.onDeleteAwardVideo(video.id)} aria-label="Delete video">
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  renderSummary() {
    if (!this.isOneItemSelected()) {
      return null;
    }

    const { currentTabIndex, selectedItems } = this.state;
    const award = selectedItems[0];
    const recipient = award.student?.displayName || (award.houseName ? `${award.houseName} house` : 'School award');
    const galleryCount = this.state.photos.length + this.state.videos.length + this.getPending(award.id).length;
    const fields = [
      ['Award', award.awardType?.name || 'Award'],
      ['Recipient', recipient],
      ['House', award.houseName || '—'],
      ['Points', this.renderPointsPill(award.points)],
      ['Awarded', moment(award.awardedAt).format('DD MMM YYYY')],
      ['Granted by', award.awardedByName || '—'],
      ['Reason', award.reason || '—']
    ];

    return (
      <aside className="schoolAwardsDetails">
        <div className="schoolAwardsDetails__header">
          {this.renderAwardEmblem(
            award.awardType?.name || 'Award',
            46,
            (award as any).displayPicture || award.awardType?.picture
          )}
          <div>
            <span>Award details</span>
            <h2>{award.awardType?.name || 'Award'}</h2>
            <p>
              {recipient}
              <i />
              {moment(award.awardedAt).format('DD MMM YYYY')}
            </p>
          </div>
          <button
            type="button"
            className="schoolAwardsDetails__close"
            onClick={this.closeDetailsDrawer}
            aria-label="Close details"
          >
            ×
          </button>
        </div>

        <div className="schoolAwardsDetails__tabs">
          <button
            type="button"
            className={
              currentTabIndex === 0
                ? 'schoolAwardsDetails__tab schoolAwardsDetails__tab--active'
                : 'schoolAwardsDetails__tab'
            }
            onClick={() => this.setState({ currentTabIndex: 0 })}
          >
            Summary
          </button>
          <button
            type="button"
            className={
              currentTabIndex === 1
                ? 'schoolAwardsDetails__tab schoolAwardsDetails__tab--active'
                : 'schoolAwardsDetails__tab'
            }
            onClick={() => this.setState({ currentTabIndex: 1 })}
          >
            Gallery{galleryCount ? <span>{galleryCount}</span> : null}
          </button>
        </div>

        <div className="schoolAwardsDetails__body">
          {currentTabIndex === 0 ? (
            <div className="schoolAwardsDetailFields">
              {fields.map(([label, value], index) => (
                <div className="schoolAwardsDetailField" key={String(label)}>
                  <span>{label}</span>
                  {React.isValidElement(value) ? value : <strong>{value}</strong>}
                </div>
              ))}
            </div>
          ) : (
            this.renderGalleryTab()
          )}
        </div>

        <div className="schoolAwardsDetails__footer">
          <button
            type="button"
            className="schoolAwardsButton schoolAwardsButton--secondary"
            onClick={() => this.setState({ currentTabIndex: 1 })}
          >
            Gallery
          </button>
          <button type="button" className="schoolAwardsButton schoolAwardsButton--primary" onClick={this.onEditClick}>
            Edit award
          </button>
          <button
            type="button"
            className="schoolAwardsButton schoolAwardsButton--danger"
            onClick={this.onDeleteModalOpen}
          >
            Delete
          </button>
        </div>
      </aside>
    );
  }
  renderDeleteModal() {
    const { isDeleteModalOpen, selectedItems } = this.state;
    if (!isDeleteModalOpen) return null;
    const single = selectedItems.length === 1;
    const awardName = single ? selectedItems[0]?.awardType?.name || 'this award' : `${selectedItems.length} awards`;

    return (
      <div className="schoolAwardsModalOverlay" onMouseDown={this.onDeleteModalClose}>
        <div className="schoolAwardsModal" onMouseDown={event => event.stopPropagation()}>
          <button
            type="button"
            className="schoolAwardsModal__close"
            onClick={this.onDeleteModalClose}
            aria-label="Close"
          >
            ×
          </button>
          <div className="schoolAwardsModal__icon">!</div>
          <h2>Delete {single ? 'award event' : 'award events'}?</h2>
          <p>
            This removes {single ? `"${awardName}"` : awardName} from this year’s award history. This action cannot be
            undone.
          </p>
          <div className="schoolAwardsModal__actions">
            <button
              type="button"
              className="schoolAwardsButton schoolAwardsButton--ghost"
              onClick={this.onDeleteModalClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="schoolAwardsButton schoolAwardsButton--danger"
              onClick={this.onDeleteConfirm}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  }

  render() {
    if (this.state.isLoading) return <Loader />;

    const { totalWithFilters, awardView, isDataFiltered, totalPoints, totalStudents } = this.state;
    const isArchiveAwaitingFilters = awardView === 'archive' && !isDataFiltered;

    return (
      <div className="schoolAwardsPage">
        {this.renderEditModal()}
        {this.renderDeleteModal()}

        <header className="schoolAwardsHero">
          <h1>Awards</h1>
          <nav className="schoolAwardsHero__tabs" aria-label="Award lists">
            <button
              type="button"
              className={awardView === 'current' ? 'is-active' : ''}
              onClick={() => this.onAwardsViewChange('current')}
            >
              Current
              {awardView === 'current' && <span>{totalWithFilters.toLocaleString()}</span>}
            </button>
            <button
              type="button"
              className={awardView === 'archive' ? 'is-active' : ''}
              onClick={() => this.onAwardsViewChange('archive')}
            >
              Archive
              {awardView === 'archive' && !isArchiveAwaitingFilters && <span>{totalWithFilters.toLocaleString()}</span>}
            </button>
          </nav>
          {!isArchiveAwaitingFilters && (
            <div className="schoolAwardsHero__chips">
              {awardView === 'current' && (
                <span>
                  Academic year <b>{getCurrentAcademicYearLabel()}</b>
                </span>
              )}
              <span>
                <b>{totalPoints.toLocaleString()}</b> points
              </span>
              <span>
                <b>{totalStudents.toLocaleString()}</b> students
              </span>
            </div>
          )}
        </header>

        <div className="schoolAwardsLayout">{this.renderTable()}</div>
        {this.renderSummary()}
      </div>
    );
  }
}
